import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { secureGet, secureDelete, securePost, secureGetPdf } from "../../config/api";
import { useHistory } from "react-router-dom";
import { PrimaryButton } from "../../components/PrimaryButton";
import { formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import OptionsMenu from "../../components/OptionsMenu";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import TableLoadingArea from "../../components/TableLoadingArea";
import StatusChain, { STATUS_COLORS as CHAIN_COLORS } from "../../components/StatusChain";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";
import UpdateSageBCPopup from "../../components/UpdateSageBCPopup";

import AddIcon from "../../icons/plusB.svg";
import DownloadIcon from "../../icons/downloadB.svg";
import EyeIcon from "../../icons/eye.svg";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";
import checkIcon from "../../icons/checkB.svg";
import InfoIcon from "../../icons/info.svg";

const Orders = () => {
  const [, setOrdersData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientId: null,
    commercialId: null,
    dateRange: {},
    generatedDeliveryNote: null,
    status: [],
    repoIds: [],
    needle: "",
  });
  const [clients, setClients] = useState([]);
  const [commercials, setCommercials] = useState([]);
  const [repos, setRepos] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const { state: { role } } = useContext(UserContext);
  const [isUpdateSageBCPopupOpen, setIsUpdateSageBCPopupOpen] = useState(false);
  const [BCtoUpdate, setBCtoUpdate] = useState(null);
  const [updatingSageCode, setUpdatingSageCode] = useState(false);  
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isCommercial = role === "ROLE_COMMERCIAL";

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const STATUS_COLORS = {
    CREATED: "#0283F4",
    UNDER_APPROVAL: "#FEA26F",
    VALIDATED: "#97DFFC",
    DELIVERED: "#06D6A0",
    DECLINED: "#EF466F",
    CANCELLED: "#A8ADC3",
  };

  const STATE_COLORS = {
    true: "#06D6A0",
    false: "#ADAFC6",
  };

  const getStateStyle = (status, color) => ({
    backgroundColor: color || STATUS_COLORS[status],
  });

  const renderStatut = (order) => {
    const quotesExist = order.quotes && order.quotes.length > 0;
    const deliveryNoteExists = order.deliveryNote !== null;

    return (
      <StatusChain
        items={[
          { key: "DV", label: "DV", color: quotesExist ? CHAIN_COLORS.active : CHAIN_COLORS.missing },
          { key: "BC", label: "BC" },
          { key: "BL", label: "BL", color: deliveryNoteExists ? CHAIN_COLORS.active : CHAIN_COLORS.missing },
        ]}
      />
    );
  };

  const getStatusLabel = (status) => t(`order_status.${status}`);
  const getStateLabel = (state) => t(`treated_status.${state}`);

  const columns = [
    { label: t(`columns.cartNumber`), key: "cartNumber" },
    {
      label: t(`columns.state`),
      key: "status",
      render: (status, row) => (
        <div>
          <span
            style={getStateStyle(status)}
            className="text-white text-[12px] font-semibold px-2 py-1 rounded-full  inline-block w-fit text-center"
          >
            {getStatusLabel(status).toUpperCase()}
          </span>

          {status === "UNDER_APPROVAL" && row.motifs && row.motifs.length > 0 && (
            <div className="mt-1 text-[8px] text-gray-500">
              <div className="flex flex-wrap">
                {(row.motifs).map((motif, index) => (
                  <span className="text-white text-[8px]  mb-1 font-semibold px-2 py-1 rounded-full  inline-block w-fit text-center"
                    key={index} style={getStateStyle(status, "#ef466f")}>
                    {t(`motifs.${motif}`)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ),
      style: { maxWidth: "150px" }
    },
    {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    { label: "Client", key: "client", render: (client) => client?.name || "N/A" },
    { label: t(`columns.commercial`), key: "user", render: (user) => `${user.firstName} ${user.lastName}` },
    { label: t(`columns.stock`), key: "repo", render: (repo) => repo?.name || "N/A" },
    { label: "Total HT", key: "priceHT", },
    { label: "Total TTC", key: "priceTTC", },
    {
      label: t(`columns.status`),
      render: (_, rowData) => renderStatut(rowData),
    },
    {
      label: t("columns.state"), key: "treated",
      render: (treated) => {
        return (
          <span
            className="text-white text-[12px] font-semibold px-2 py-1 rounded-full  inline-block w-fit text-center"
            style={{ backgroundColor: STATE_COLORS[treated] || "#A8ADC3" }}
          >
            {getStateLabel(treated).toUpperCase()}
          </span>
        );
      },
    },
    {
      key: "actions",
      render: (_, rowData) =>
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
    },
  ];

  const handleOpenPopup = (item) => {
    setBCtoUpdate(item);
    setIsUpdateSageBCPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsUpdateSageBCPopupOpen(false);
  };

  const handleUpdateSageCode = async (code) => {
    const payload = {
      cartId: BCtoUpdate.id,
      note: code
    }
    setUpdatingSageCode(true);
    try {
      const response = await securePost(`/carts/updateSageCode`, payload, showSnackbar);
      showSnackbar(t(`confirmation.sageCodeUpdate`), 'success');
      setBCtoUpdate(null);
    } catch (error) {
      console.error("Error updating Sage Code:", error);
    } finally {
      setUpdatingSageCode(false);
      handleClosePopup();
    }
  };

  const handleTreat = async (item) => {
    const payload = {
      cartId: item.id,
      treated: true,
    };
    try {
      const response = await securePost(`/carts/updateTreated`, payload, showSnackbar);
      showSnackbar(t(`confirmation.treatSuccess`), 'success');
      fetchPaginatedOrders(currentPage, pageSize, filters);
    } catch (error) {
      console.error("Error treating order:", error);
    }
  };

  const getOptions = (item) => {
    const options = [];
    const deliveryNoteExists = item.deliveryNote !== null;

    if (role === "ROLE_AGENT") {
      options.push(
        { label: t(`actions.updateSageCode`), icon: InfoIcon, action: () => handleOpenPopup(item) }
      );
      if (item.status === "DELIVERED" && item.treated === false) {
        options.push(
          { label: t(`actions.treat`), icon: checkIcon, action: () => handleTreat(item) }
        );
      }
    }

    if (item.status === "CREATED" || item.status === "UNDER_APPROVAL") {
      options.push(
        { label: t(`actions.details`), icon: EyeIcon, action: () => handleDetails(item) }
      );
      if (role !== "ROLE_AGENT") {
        options.push({ label: t(`actions.modify`), icon: EditIcon, action: () => handleEdit(item) });
      }
      options.push({ label: t(`actions.delete`), color: '#EF466F', icon: DeleteIcon, action: () => handleDelete(item) });

    } else if (item.status === "DECLINED") {
      options.push(
        { label: t(`actions.details`), icon: EyeIcon, action: () => handleDetails(item) },
        { label: t(`actions.download_order`), icon: DownloadIcon, action: () => handleDownload(item) },
        { label: t(`actions.delete`), color: '#EF466F', icon: DeleteIcon, action: () => handleDelete(item) }
      );

    } else if (item.status === "CANCELLED") {
      options.push(
        { label: t(`actions.details`), icon: EyeIcon, action: () => handleDetails(item) },
        { label: t(`actions.download_order`), icon: DownloadIcon, action: () => handleDownload(item) },
      );
      if (role !== "ROLE_AGENT") {
        options.push({ label: t(`actions.modify`), icon: EditIcon, action: () => handleEdit(item) });
      }

    } else if (item.status === "DELIVERED") {
      options.push(
        { label: t(`actions.details`), icon: EyeIcon, action: () => handleDetails(item) },
        { label: t(`actions.download_order`), icon: DownloadIcon, action: () => handleDownload(item) }
      );

      if (!deliveryNoteExists && role !== "ROLE_AGENT") {
        options.push(
          { label: t(`actions.modify`), icon: EditIcon, action: () => handleEdit(item) },
          { label: t(`actions.delete`), color: '#EF466F', icon: DeleteIcon, action: () => handleDelete(item) }
        );
      }

    } else if (item.status === "VALIDATED") {
      options.push(
        { label: t(`actions.details`), icon: EyeIcon, action: () => handleDetails(item) }
      );
      if (!deliveryNoteExists && role !== "ROLE_AGENT") {
        options.push({ label: t(`actions.modify`), icon: EditIcon, action: () => handleEdit(item) });
      }
      options.push(
        { label: t(`actions.download_order`), icon: DownloadIcon, action: () => handleDownload(item) },
        { label: t(`actions.delete`), color: '#EF466F', icon: DeleteIcon, action: () => handleDelete(item) }
      );
    }
    return options;
  };

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
    },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: [...clients.map((client) => ({ value: client.id, reference: client.code, name: client.name }))],
    },
    {
      name: "commercialId",
      label: t(`filters.orders.commercial`),
      type: "dropdown",
      options: isCommercial
        ? [
          {
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
          },
        ]
        : commercials.map((commercial) => ({
          value: commercial.id,
          label: `${commercial.firstName} ${commercial.lastName}`,
        })),
    },
    {
      name: "status",
      label: t(`filters.orders.state`),
      type: "checkbox-dropdown",
      options: [
        { value: "CREATED", label: t(`order_status.CREATED`) },
        { value: "UNDER_APPROVAL", label: t(`order_status.UNDER_APPROVAL`) },
        { value: "VALIDATED", label: t(`order_status.VALIDATED`) },
        { value: "DELIVERED", label: t(`order_status.DELIVERED`) },
        { value: "DECLINED", label: t(`order_status.DECLINED`) },
        { value: "CANCELLED", label: t(`order_status.CANCELLED`) },
      ],
    },
    {
      name: "generatedDeliveryNote",
      label: t(`filters.orders.status`),
      type: "dropdown",
      options: [
        { value: true, label: t(`bl_status.true`) },
        { value: false, label: t(`bl_status.false`) },
      ],
    },
    {
      name: "repoIds",
      label: t(`filters.orders.stock`),
      type: "checkbox-dropdown",
      options: repos.map((repo) => ({ value: repo.id, label: repo.name })),
    },
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const clientResponse = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientResponse.data);

        if (!isCommercial) {
          const commercialResponse = await secureGet("/users/ROLE_COMMERCIAL", showSnackbar, { signal });
          setCommercials(commercialResponse.data);
        }

        const repoResponse = await secureGet("/repos", showSnackbar, { signal });
        setRepos(repoResponse.data);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };
    fetchDropdownOptions();
    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchPaginatedOrders = useCallback(
    async (page = 0, size = pageSize, activeFilters = filters) => {
      setLoading(true);



      try {
        const requestBody = {
          clientId: activeFilters.clientId ?? null,
          commercialId: activeFilters.commercialId ?? null,
          startDate: activeFilters.startDate ?? null,
          endDate: activeFilters.endDate ?? null,
          generatedDeliveryNote:
            activeFilters.generatedDeliveryNote ?? null,
          status:
            activeFilters.status?.length > 0
              ? activeFilters.status
              : null,
          repoIds:
            activeFilters.repoIds?.length > 0
              ? activeFilters.repoIds
              : null,
          needle: activeFilters.needle ?? null,
        };


        const response = await securePost(`/carts/all/paged`, requestBody, showSnackbar, {
          params: { page, size },
          signal,
        });
        const { content, totalPages } = response.data;
        console.log(content)
        setOrdersData(content);
        setCurrentPageData(content);
        setTotalPages(totalPages);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching orders:", error);
        }
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize, signal, role]
  );

  useEffect(() => {
    const stateFilters = location.state?.filters || {
      clientId: null,
      commercialId: null,
      dateRange: {},
      generatedDeliveryNote: null,
      status: [],
      repoIds: [],
      needle: "",
    };
    const statePage = location.state?.page ?? 0;
    const stateSize = location.state?.size ?? 30;

    fetchPaginatedOrders(statePage, stateSize, stateFilters);

    setFilters(stateFilters);
    setCurrentPage(statePage);
    setPageSize(stateSize);
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedOrders(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedOrders(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else if (filterName === "status") {
      updatedFilters.status = filterValue;
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedOrders(0, pageSize, updatedFilters);
  };


  const resetFilters = () => {
    const defaultFilters = {
      clientId: null,
      commercialId: null,
      dateRange: {},
      generatedDeliveryNote: null,
      status: [],
      repoIds: [],
      needle: "",
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedOrders(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await secureDelete(`/carts/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaginatedOrders(currentPage, pageSize);
      showSnackbar(t(`confirmation.orderDelete`), 'success');
    } catch (error) {
      console.error("Error deleting item:", error);
      setDeletePopupVisible(false);
    } finally {
      setDeleting(false);
      setItemToDelete(null);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/orders/details/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };

  const handleEdit = (item) => {
    history.push({
      pathname: `/orders/modify/${item.id}`,
      state: { filters, page: currentPage, size: pageSize },
    });
  };

  const handleDownload = async (item) => {
    try {
      const response = await secureGetPdf(`/carts/bl/${item.id}`, showSnackbar);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `commande/${item.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download BL:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.orders")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.orders`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`primaryButtons.add_order`)}
            icon={AddIcon}
            onClick={() => history.push("/orders/add")}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <TableLoadingArea loading={loading}>
        <Table
          columns={columns}
          data={currentPageData}
          onRowClick={(row) => {
            history.push({
              pathname: `/orders/details/${row.id}`,
              state: { filters, page: currentPage, size: pageSize },
            });
          }}
        />
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible} onCancel={() => setDeletePopupVisible(false)} onConfirm={handleDeleteConfirm} loading={deleting} />
      )}

      <UpdateSageBCPopup
        open={isUpdateSageBCPopupOpen}
        onConfirm={(newCode) => handleUpdateSageCode(newCode)}
        onCancel={handleClosePopup}
        saving={updatingSageCode}
      />


      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Orders;

